import { useEffect, useMemo, useState } from "react";
import { format, parseISO, subDays, differenceInCalendarDays, startOfToday } from "date-fns";
import { toast } from "sonner";
import { Loader2, CalendarDays, CheckCircle2 } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { http, formatApiError } from "@/lib/api";
import { naira } from "@/components/site/ShortletCard";

const field = "mt-2 w-full bg-transparent border border-gw-rule/40 p-3 text-base outline-none focus:border-gw-rule transition-colors duration-200";
const iso = (d) => format(d, "yyyy-MM-dd");

const useUnavailable = (id) => {
  const [ranges, setRanges] = useState([]);
  useEffect(() => {
    http.get(`/shortlets/${id}/unavailable`).then((r) => setRanges(Array.isArray(r.data) ? r.data : [])).catch(() => setRanges([]));
  }, [id]);
  return ranges;
};

const Summary = ({ s, nights }) => {
  const stay = nights * (s.nightly_rate || 0);
  return (
    <dl className="mt-6 border-t border-gw-rule/25 text-sm" data-testid="booking-summary">
      <div className="flex justify-between py-2"><dt>{naira(s.nightly_rate)} × {nights} night{nights === 1 ? "" : "s"}</dt><dd>{naira(stay)}</dd></div>
      {s.cleaning_fee > 0 && <div className="flex justify-between py-2"><dt>Cleaning fee</dt><dd>{naira(s.cleaning_fee)}</dd></div>}
      <div className="flex justify-between py-3 border-t border-gw-rule/25 lux text-xl"><dt>Total</dt><dd data-testid="booking-total">{naira(stay + (s.cleaning_fee || 0))}</dd></div>
    </dl>
  );
};

const Done = () => (
  <div className="border border-gw-rule/30 p-6" data-testid="booking-request-success">
    <CheckCircle2 size={28} />
    <p className="lux text-2xl mt-4">Request sent.</p>
    <p className="gw-body mt-3 opacity-80">We'll check the dates and email you a secure payment link once they're approved. Nothing is charged until then.</p>
  </div>
);

export const BookingWidget = ({ s }) => {
  const ranges = useUnavailable(s.id);
  const [range, setRange] = useState();
  const [form, setForm] = useState({ guests: 1, name: "", email: "", phone: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const disabled = useMemo(() => [{ before: startOfToday() },
    ...ranges.map((r) => ({ from: parseISO(r.check_in), to: subDays(parseISO(r.check_out), 1) }))], [ranges]);
  const nights = range?.from && range?.to ? differenceInCalendarDays(range.to, range.from) : 0;
  const clash = nights > 0 && ranges.some((r) => r.check_in < iso(range.to) && r.check_out > iso(range.from));
  const tooShort = nights > 0 && nights < (s.min_nights || 1);

  const submit = async (e) => {
    e.preventDefault();
    if (!nights || clash || tooShort) return;
    setBusy(true);
    try {
      await http.post("/bookings", { ...form, guests: Number(form.guests), shortlet_id: s.id, check_in: iso(range.from), check_out: iso(range.to) });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) return <Done />;
  return (
    <form onSubmit={submit} className="border border-gw-rule/30 p-5 md:p-6" data-testid="booking-widget">
      <p className="lab inline-flex items-center gap-2"><CalendarDays size={14} /> Choose your dates</p>
      <div className="mt-3 flex justify-center border border-gw-rule/20">
        <Calendar mode="range" selected={range} onSelect={setRange} disabled={disabled} numberOfMonths={1} data-testid="booking-calendar"
          classNames={{ day_selected: "bg-gw-btn text-gw-btnfg hover:bg-gw-btn hover:text-gw-btnfg focus:bg-gw-btn focus:text-gw-btnfg",
            day_range_middle: "aria-selected:bg-gw-hover aria-selected:text-gw-ink", day_disabled: "opacity-30 line-through" }} />
      </div>
      <p className="text-sm mt-3 opacity-80" data-testid="booking-dates-text">
        {range?.from ? `${format(range.from, "d MMM yyyy")} → ${range?.to ? format(range.to, "d MMM yyyy") : "pick check-out"}` : `Minimum stay ${s.min_nights || 1} night${(s.min_nights || 1) > 1 ? "s" : ""}. Crossed-out dates are taken.`}
      </p>
      {clash && <p className="text-sm mt-2 text-red-600 dark:text-red-300" data-testid="booking-clash-error">Your dates include nights that are already booked.</p>}
      {tooShort && <p className="text-sm mt-2 text-red-600 dark:text-red-300" data-testid="booking-min-error">Please choose at least {s.min_nights} nights.</p>}
      {nights > 0 && !clash && !tooShort && <Summary s={s} nights={nights} />}
      <div className="grid sm:grid-cols-2 gap-4 mt-6">
        <label className="lab">Guests<input type="number" min={1} max={s.max_guests || 2} required value={form.guests} onChange={set("guests")} className={field} data-testid="booking-guests-input" /></label>
        <label className="lab">Full name<input required maxLength={120} value={form.name} onChange={set("name")} className={field} data-testid="booking-name-input" /></label>
        <label className="lab">Email<input type="email" required value={form.email} onChange={set("email")} className={field} data-testid="booking-email-input" /></label>
        <label className="lab">Phone<input required minLength={5} maxLength={40} value={form.phone} onChange={set("phone")} className={field} data-testid="booking-phone-input" /></label>
        <label className="lab sm:col-span-2">Note (optional)<textarea rows={2} maxLength={2000} value={form.note} onChange={set("note")} className={field} data-testid="booking-note-input" /></label>
      </div>
      <button type="submit" disabled={busy || !nights || clash || tooShort} data-testid="booking-submit-button"
        className="mt-6 w-full lab bg-gw-btn text-gw-btnfg px-6 py-4 inline-flex justify-center items-center gap-2 transition-colors duration-200 hover:bg-gw-btnhover disabled:opacity-50">
        {busy && <Loader2 size={14} className="animate-spin" />}Request to book
      </button>
      <p className="text-xs mt-3 opacity-70">No payment now. Once we approve your dates, you'll get a link to pay by card or Paystack.</p>
    </form>
  );
};
